/**
 * Data layer for `lifetime-commission`.
 * Thin typed fetch wrappers over /api/v1/lifetime-commission.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 *
 * BE shape (apps/api/src/routes/lifetime-commission.ts):
 *   GET  /api/v1/lifetime-commission                 -> { items: Row[] }
 *   POST /api/v1/lifetime-commission                 -> { ok, customer_id, ... }
 *   GET  /api/v1/lifetime-commission/affiliate/:id   -> aggregate rollup
 * Tenant is pinned to `t1` via the `x-tenant-id` header.
 */

export interface LifetimeCommissionRow {
	id: number;
	tenant_id: string;
	customer_id: string;
	affiliate_id: string;
	campaign_id: string | null;
	first_referral_at: number;
	commission_pct_bps: number;
	lifetime_value_cents: number;
	commission_earned_cents: number;
	created_at: number;
	updated_at: number;
}

export interface ListLifetimeCommissionResponse {
	items: LifetimeCommissionRow[];
}

export interface CreateLifetimeCommissionBody {
	customer_id: string;
	affiliate_id: string;
	commission_pct_bps: number;
	lifetime_value_cents: number;
	campaign_id?: string | null;
}

export interface CreateLifetimeCommissionResponse {
	ok: true;
	customer_id: string;
	affiliate_id: string;
	lifetime_value_cents: number;
	commission_earned_cents: number;
	updated_at: number;
}

const BASE = "/api/v1/lifetime-commission";
const TENANT_HEADERS = { "x-tenant-id": "t1" } as const;

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListLifetimeCommissionResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json", ...TENANT_HEADERS },
	});
	return parseOrThrow<ListLifetimeCommissionResponse>(res);
}

export async function create(
	body: CreateLifetimeCommissionBody,
): Promise<CreateLifetimeCommissionResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
			...TENANT_HEADERS,
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateLifetimeCommissionResponse>(res);
}

/**
 * BE has no DELETE endpoint yet; expose a no-op stub so the TanStack Query hook
 * contract stays stable (future BE DELETE will slot in without a UI rewrite).
 */
export async function remove(id: string): Promise<{ removed: boolean }> {
	void id;
	throw new Error("delete not supported yet");
}

export const lifetimeCommissionQueryKeys = {
	all: ["lifetime-commission"] as const,
	list: () => [...lifetimeCommissionQueryKeys.all] as const,
	detail: (id: string) => [...lifetimeCommissionQueryKeys.all, id] as const,
};
